import { ListItemNode, ListNode } from '@lexical/list'
import { HEADING, ORDERED_LIST, UNORDERED_LIST } from '@lexical/markdown'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin'
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { HeadingNode } from '@lexical/rich-text'
import { $isRootTextContentEmpty } from '@lexical/text'
import { $createParagraphNode, $getRoot, CLEAR_EDITOR_COMMAND, EditorState, InitialEditorStateType, LexicalEditor as LexicalEditorType } from 'lexical'
import { RefObject, useState } from 'react'

import { EnterKeyPlugin } from './EnterKeyPlugin'
import { LexicalRichTextInput, LexicalRichTextInputProps } from './LexicalRichTextInput'
import { MentionNode } from './mentions/MentionNode'
import { MentionsPlugin } from './mentions/MentionsPlugin'
import { $trimLastNode } from './utils'

const theme = {
    text: {
        bold: 'font-bold',
        italic: 'italic',
        underline: 'underline',
    },
    list: {
        ul: 'list-disc list-inside',
        ol: 'list-decimal list-inside',
        listitem: 'ml-4',
    },
    heading: {
        h1: 'text-2xl font-bold mb-2',
        h2: 'text-xl font-bold mb-2',
        h3: 'text-lg font-bold mb-2',
    },
}

// Only use list and heading transformers
const MARKDOWN_TRANSFORMERS = [HEADING, UNORDERED_LIST, ORDERED_LIST]

const onError = (error: Error) => {
    console.error(error)
}

export type LexicalEditorProps = Omit<LexicalRichTextInputProps, 'onSendMessage'> & {
    onChange?: (editorState: EditorState, editor: LexicalEditorType) => void
    initialValue?: Exclude<InitialEditorStateType, (editor: LexicalEditorType) => void>
    editable?: boolean
    onSendMessage?: (editor: LexicalEditorType) => void | Promise<void>
    inputRef?: RefObject<HTMLDivElement | null>
    autoFocus?: boolean
    rootPortalRef?: RefObject<HTMLDivElement | null>
}

// when the initial value is a string, we need to parse it into an editor state.
// This can fail if JSON is corrupt, or even just message is not in the format.
// if so we just fallback to the content being a string of the value. This works well to preserve older messages.
// however it could be confusing if it's due to a corrupt json.
const createParseEditorStateFunction = (initialValue: string) => (editor: LexicalEditorType) => {
    try {
        editor.setEditorState(editor.parseEditorState(initialValue))
    } catch {
        const paragraph = $createParagraphNode()
        paragraph.select().insertText(initialValue)
        $getRoot().append(paragraph)
    }
}

const ComposedEditor = ({
    onChange,
    initialValue,
    editable = true,
    onSendMessage,
    autoFocus,
    rootPortalRef,
    ...lexicalRichTextInputProps
}: LexicalEditorProps) => {
    const initialConfig: InitialConfigType = {
        namespace: 'CommentEditor',
        theme,
        onError,
        editorState: typeof initialValue === 'string' ? createParseEditorStateFunction(initialValue) : initialValue,
        editable,
        nodes: [ListNode, ListItemNode, HeadingNode, MentionNode],
    }

    const [isSending, setIsSending] = useState(false)

    const handleSendMessage = async (editor: LexicalEditorType) => {
        editor.update(() => $trimLastNode(), { discrete: true })
        const isEmpty = editor.read(() => $isRootTextContentEmpty(editor.isComposing()))

        if (isEmpty || isSending) {
            return
        }

        editor.focus()
        setIsSending(true)

        try {
            await onSendMessage?.(editor)
            editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined)
            editor.focus()
        } finally {
            setIsSending(false)
        }
    }

    return (
        <LexicalComposer initialConfig={initialConfig} key={`lexical-editable-${editable}`}>
            <div className="relative leading-6">
                <RichTextPlugin
                    contentEditable={
                        editable ? (
                            <LexicalRichTextInput
                                onSendMessage={onSendMessage ? handleSendMessage : undefined}
                                isLoading={isSending}
                                {...lexicalRichTextInputProps}
                            />
                        ) : (
                            <ContentEditable />
                        )
                    }
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <HistoryPlugin />
                {autoFocus && <AutoFocusPlugin />}
                <ListPlugin />
                <MarkdownShortcutPlugin transformers={MARKDOWN_TRANSFORMERS} />
                <ClearEditorPlugin />
                <MentionsPlugin rootPortalRef={rootPortalRef} />
                {editable && onSendMessage && <EnterKeyPlugin onSendMessage={handleSendMessage} />}
                {onChange && <OnChangePlugin onChange={onChange} />}
            </div>
        </LexicalComposer>
    )
}

export const LexicalEditor = (props: LexicalEditorProps) => (
    <LexicalErrorBoundary onError={onError}>
        <ComposedEditor {...props} />
    </LexicalErrorBoundary>
)
